/**
 * Lightweight, cookieless A/B testing.
 *
 * A stable anonymous id is stored in localStorage and hashed with the
 * experiment id, so each visitor is assigned the same variant on every visit
 * and no server round-trip is required.
 */
import { trackEvent } from "./analytics";

const ID_KEY = "vb_ab_id";
const EXPOSURE_KEY = "vb_ab_exposure";

export const CTA_PLACEMENT_EXPERIMENT = "cta_placement_v1";

/** Variants for the CTA placement test on long service pages. */
export type CtaPlacementVariant = "mid_page" | "bottom_only";

export const CTA_PLACEMENT_VARIANTS: CtaPlacementVariant[] = ["mid_page", "bottom_only"];

function readStorage(store: Storage | undefined, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStorage(store: Storage | undefined, key: string, value: string) {
  try {
    store?.setItem(key, value);
  } catch {
    /* storage blocked — variant falls back to per-render assignment */
  }
}

/** Stable anonymous visitor id (not personally identifying). */
export function getVisitorId(): string | null {
  if (typeof window === "undefined") return null;
  const existing = readStorage(window.localStorage, ID_KEY);
  if (existing) return existing;
  const generated =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  writeStorage(window.localStorage, ID_KEY, generated);
  return generated;
}

/** Deterministic 32-bit hash (FNV-1a). */
function hash(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Assign a variant for an experiment. Returns null during SSR. */
export function getVariant<T extends string>(experiment: string, variants: T[]): T | null {
  if (typeof window === "undefined") return null;
  const id = getVisitorId();
  if (!id) return variants[0];
  const override = new URLSearchParams(window.location.search).get("ab_variant");
  if (override && (variants as string[]).includes(override)) return override as T;
  return variants[hash(`${experiment}:${id}`) % variants.length];
}

type Exposure = {
  experiment: string;
  variant: string;
  page_path: string;
  ts: number;
};

/** Record (and de-duplicate per page view) that a visitor saw a variant. */
export function trackExposure(experiment: string, variant: string) {
  if (typeof window === "undefined") return;
  const pagePath = window.location.pathname;
  const payload: Exposure = { experiment, variant, page_path: pagePath, ts: Date.now() };
  writeStorage(window.sessionStorage, EXPOSURE_KEY, JSON.stringify(payload));
  const seenKey = `${EXPOSURE_KEY}:${experiment}:${pagePath}`;
  if (readStorage(window.sessionStorage, seenKey)) return;
  writeStorage(window.sessionStorage, seenKey, "1");
  trackEvent("experiment_impression", {
    experiment_id: experiment,
    variant_id: variant,
    page_path: pagePath,
  });
}

/** Fired when a visitor clicks a CTA that is part of an experiment. */
export function trackVariantCtaClick(params: {
  experiment: string;
  variant: string;
  placement: "mid_page" | "bottom";
  label: string;
  destination: string;
}) {
  if (typeof window === "undefined") return;
  writeStorage(
    window.sessionStorage,
    EXPOSURE_KEY,
    JSON.stringify({
      experiment: params.experiment,
      variant: params.variant,
      page_path: window.location.pathname,
      ts: Date.now(),
    } satisfies Exposure),
  );
  trackEvent("experiment_cta_click", {
    experiment_id: params.experiment,
    variant_id: params.variant,
    cta_placement: params.placement,
    cta_label: params.label,
    link_url: params.destination,
    page_path: window.location.pathname,
  });
}

/**
 * Fire a conversion for the most recent exposure (called from /appointments).
 * De-duplicated per session + conversion step.
 */
export function trackExperimentConversion(step: string) {
  if (typeof window === "undefined") return;
  const raw = readStorage(window.sessionStorage, EXPOSURE_KEY);
  if (!raw) return;
  let exposure: Exposure;
  try {
    exposure = JSON.parse(raw) as Exposure;
  } catch {
    return;
  }
  const dedupeKey = `${EXPOSURE_KEY}:conv:${exposure.experiment}:${step}`;
  if (readStorage(window.sessionStorage, dedupeKey)) return;
  writeStorage(window.sessionStorage, dedupeKey, "1");
  trackEvent("experiment_conversion", {
    experiment_id: exposure.experiment,
    variant_id: exposure.variant,
    conversion_step: step,
    source_page_path: exposure.page_path,
    seconds_since_exposure: Math.round((Date.now() - exposure.ts) / 1000),
  });
}
